import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, CalendarDays, ClipboardCheck, Clock, Inbox, Users2, type LucideIcon } from "lucide-react";

import type { MeetingDraft } from "../components/meetings/MeetingForms";
import { AvailabilityPanel, MeetingsPanel, RequestsPanel } from "../components/meetings/MeetingPanels";
import { usePermissions } from "../hooks/usePermissions";
import { meetingsService } from "../services/meetingsService";
import { useAuthStore } from "../stores/authStore";

type Tab = "meetings" | "requests" | "availability";
const LEADERSHIP = ["admin", "director", "head_teacher", "director_of_studies"];

/**
 * Parent–teacher meetings. Teachers schedule meetings about the learners they
 * teach (or answer a parent's request), parents confirm, and the teacher
 * records what was agreed and any follow-up.
 */
export default function Meetings() {
  const { hasCapability } = usePermissions();
  const staff = hasCapability("access.core.academic_staff");
  const user = useAuthStore((state) => state.user);
  const isLeader = LEADERSHIP.includes(user?.user_type ?? "");
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") ?? undefined;
  const [draft, setDraft] = useState<MeetingDraft | null>(null);

  const stats = useQuery({ queryKey: ["meetings", "stats"], queryFn: meetingsService.getStats });
  const types = useQuery({ queryKey: ["meetings", "types"], queryFn: meetingsService.getTypes });
  const meetings = useQuery({ queryKey: ["meetings", "list"], queryFn: meetingsService.getMeetings });
  const requests = useQuery({ queryKey: ["meetings", "requests"], queryFn: meetingsService.getRequests });
  const availability = useQuery({ queryKey: ["meetings", "availability"], queryFn: meetingsService.getAvailability });
  const learners = useQuery({ queryKey: ["meetings", "learners"], queryFn: meetingsService.getLearners, enabled: staff, staleTime: 5 * 60_000 });
  const teachers = useQuery({ queryKey: ["meetings", "teachers"], queryFn: () => meetingsService.getTeachers(), enabled: isLeader, staleTime: 5 * 60_000 });
  const children = useQuery({ queryKey: ["meetings", "children"], queryFn: meetingsService.getChildren, enabled: !staff, staleTime: 5 * 60_000 });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["meetings"] });
  const setup = {
    staff, isLeader, me: user?.id, types: types.data ?? [], learners: learners.data ?? [], teachers: teachers.data ?? [],
    children: children.data ?? [], availability: availability.data ?? [], meetings: meetings.data ?? [],
  };

  const s = stats.data;
  const tabs: { id: Tab; label: string; icon: LucideIcon; badge?: number }[] = [
    { id: "meetings", label: "Meetings", icon: Users2, badge: staff ? s?.outcomes_to_record : s?.awaiting_confirmation },
    { id: "requests", label: "Requests", icon: Inbox, badge: staff ? s?.pending_requests : undefined },
    { id: "availability", label: staff ? "Meeting times" : "When teachers are free", icon: Clock },
  ];
  const tab: Tab = tabs.find((item) => item.id === params.get("tab"))?.id ?? "meetings";
  const go = (next: Tab, extra: Record<string, string> = {}) => {
    const query: Record<string, string> = { ...extra };
    if (next !== "meetings") query.tab = next;
    setParams(query, { replace: true });
  };
  const dash = (value?: number) => (stats.isLoading || value === undefined ? "–" : String(value));
  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = staff
    ? [
        { label: "Meetings today", value: dash(s?.meetings_today), note: `${s?.meetings_this_week ?? 0} in the next 7 days`, go: () => go("meetings", { view: "today" }), icon: CalendarDays },
        { label: "Awaiting parent", value: dash(s?.awaiting_confirmation), note: "Not yet confirmed", go: () => go("meetings", { view: "confirm" }), icon: CalendarCheck },
        { label: "Outcomes to record", value: dash(s?.outcomes_to_record), note: s?.follow_ups_due ? `${s.follow_ups_due} follow-ups due` : "Meetings that have taken place", go: () => go("meetings", { view: s?.outcomes_to_record ? "outcome" : "follow" }), icon: ClipboardCheck, alert: Boolean(s?.outcomes_to_record) },
        { label: "Requests waiting", value: dash(s?.pending_requests), note: "Parents asking to meet", go: () => go("requests", { view: "pending" }), icon: Inbox, alert: Boolean(s?.pending_requests) },
      ]
    : [
        { label: "Coming up", value: dash(s?.upcoming_meetings), note: "Meetings with teachers", go: () => go("meetings"), icon: CalendarDays },
        { label: "To confirm", value: dash(s?.awaiting_confirmation), note: "Say you will come", go: () => go("meetings", { view: "confirm" }), icon: CalendarCheck, alert: Boolean(s?.awaiting_confirmation) },
        { label: "Your requests", value: dash(s?.pending_requests), note: "Waiting for a teacher", go: () => go("requests", { view: "pending" }), icon: Inbox },
        { label: "Meetings held", value: dash(s?.completed_meetings), note: "With what was agreed", go: () => go("meetings", { view: "held" }), icon: ClipboardCheck },
      ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Parent–teacher meetings</h1>
        <p className="mt-1 text-sm text-gray-500">{staff ? "Meet parents about the learners you teach, answer their requests, and keep what was agreed." : "Meet your children's teachers: ask for a meeting, confirm a time, and see what was agreed."}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button key={tile.label} type="button" onClick={tile.go} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}><tile.icon className="h-5 w-5" /></span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Meeting sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => (
          <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} onClick={() => go(item.id)} className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${tab === item.id ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}>
            <item.icon className="h-4 w-4" /> {item.label}
            {item.badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{item.badge}</span> : null}
          </button>
        ))}
      </nav>

      {tab === "meetings" && <MeetingsPanel key={view} rows={meetings.data} setup={setup} isLoading={meetings.isLoading} isError={meetings.isError} onRetry={() => meetings.refetch()} onChanged={refresh} initialView={view} draft={draft} onDraftUsed={() => setDraft(null)} />}
      {tab === "requests" && <RequestsPanel key={view} rows={requests.data} setup={setup} isLoading={requests.isLoading} isError={requests.isError} onRetry={() => requests.refetch()} onChanged={refresh} initialView={view} onSchedule={(next) => { setDraft(next); go("meetings"); }} />}
      {tab === "availability" && <AvailabilityPanel rows={availability.data} setup={setup} isLoading={availability.isLoading} isError={availability.isError} onRetry={() => availability.refetch()} onChanged={refresh} />}
    </div>
  );
}
